<template>
  <div class="home-page">
    <!-- Navigation Bar -->
    <nav class="navbar">
      <div class="container">
        <div class="logo">
          <img class="logo-image" src="@/assets/FullLogo.png" alt="2GN" />
          <h1>2GN Construction</h1>
        </div>
        <ul class="nav-links" :class="{ open: menuOpen }">
          <li><a href="#home" @click="closeMenu">Home</a></li>
          <li><a href="#about" @click="closeMenu">About</a></li>
          <li><a href="#services" @click="closeMenu">Services</a></li>
          <li><a href="#projects" @click="closeMenu">Projects</a></li>
          <li><a href="#contact" @click="closeMenu">Contact</a></li>
        </ul>
        <button class="menu-toggle" @click="toggleMenu" :aria-expanded="menuOpen ? 'true' : 'false'" aria-label="Toggle menu">☰</button>
      </div>
    </nav>

    <!-- Hero Section -->
    <section id="home" class="hero">
      <div class="hero-overlay">
        <div class="hero-content">
          <h1 class="hero-title">Building Your Dreams Into Reality</h1>
          <p class="hero-subtitle">{{ dynamicHeroSubtitle }}</p>
          <div class="hero-buttons">
            <a class="btn btn-primary" href="#contact">Get A Quote</a>
            <a class="btn btn-secondary" href="#projects">Our Projects</a>
          </div>
        </div>
      </div>
    </section>

    <!-- About Section -->
    <section id="about" class="about">
      <div class="container">
        <div class="section-header">
          <h2>About Us</h2>
          <div class="underline"></div>
        </div>
        <div class="about-content">
          <div class="about-text">
            <h3>40+ Years of Excellence in Construction</h3>
            <p>
              2GN Construction is a construction company specializing in residential and 
              commercial remodels. With over 40 years of experience, we have 
              built a reputation for delivering high-quality projects on time and within budget.
            </p>
            <p>
              We are dedicated to bringing your vision to life 
              with precision, innovation, and commitment to excellence.
            </p>
          </div>
          <div class="about-image">
            <div class="image-placeholder">
              <span>🏗️</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section id="services" class="services">
      <div class="container">
        <div class="section-header">
          <h2>Our Services</h2>
          <div class="underline"></div>
          <p>Comprehensive construction solutions for all your needs</p>
        </div>
        <div class="services-grid">
          <div class="service-card" v-for="service in services" :key="service.id">
            <div class="service-icon">{{ service.icon }}</div>
            <h3>{{ service.title }}</h3>
            <p>{{ service.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Projects Section -->
    <section id="projects" class="projects">
      <div class="container">
        <div class="section-header">
          <h2>Projects</h2>
          <div class="underline"></div>
          <p>{{ dynamicProjectsIntro }}</p>
        </div>
        <div v-if="projectsLoading" class="gallery-status">Loading projects...</div>
        <div v-else-if="projectCards.length === 0" class="gallery-status">No projects yet.</div>
        <div v-else class="projects-grid">
          <div class="project-card" v-for="project in projectCards" :key="project.projectID" @click="openProjectGallery(project)">
            <div class="project-image">
              <img
                v-if="project.imageID"
                :src="imageUrl(project.imageID, { w: 640, q: 72 })"
                :srcset="imageSrcSet(project.imageID, [320, 480, 640, 900], 72)"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                :alt="project.project_name"
                loading="lazy"
                decoding="async"
              />
              <div v-else class="project-placeholder">🏗️</div>
            </div>
            <div class="project-info">
              <h3>{{ project.project_name }}</h3>
              <p>{{ project.project_description || 'Description coming soon.' }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="isGalleryOpen" class="gallery-modal-overlay" @click.self="closeProjectGallery">
      <div class="gallery-modal">
        <button class="gallery-close" @click="closeProjectGallery" aria-label="Close gallery">×</button>
        <div class="gallery-modal-header">
          <h3>{{ selectedProject.project_name }}</h3>
          <p>{{ selectedProject.project_description || 'Description coming soon.' }}</p>
        </div>
        <div v-if="selectedProjectImages.length > 0" class="gallery-modal-main">
          <button
            v-if="selectedProjectImages.length > 1"
            class="gallery-nav gallery-prev"
            @click="prevGalleryImage"
            aria-label="Previous image"
          >
            ‹
          </button>
          <img
            :src="imageUrl(selectedProjectImages[activeGalleryIndex].imageID, { w: 1400, q: 80 })"
            :srcset="imageSrcSet(selectedProjectImages[activeGalleryIndex].imageID, [640, 960, 1400, 1800], 80)"
            sizes="(max-width: 768px) 100vw, 90vw"
            :alt="selectedProject.project_name"
            decoding="async"
          />
          <button
            v-if="selectedProjectImages.length > 1"
            class="gallery-nav gallery-next"
            @click="nextGalleryImage"
            aria-label="Next image"
          >
            ›
          </button>
        </div>
        <div v-else class="gallery-modal-empty">No images available for this project yet.</div>
        <div v-if="selectedProjectImages.length > 1" class="gallery-thumbnails">
          <button
            v-for="(image, index) in selectedProjectImages"
            :key="image.imageID"
            class="gallery-thumb"
            :class="{ active: index === activeGalleryIndex }"
            @click="setGalleryImage(index)"
            :aria-label="`View image ${index + 1}`"
          >
            <img
              :src="imageUrl(image.imageID, { w: 320, q: 68 })"
              :srcset="imageSrcSet(image.imageID, [120, 180, 240, 320], 68)"
              sizes="80px"
              :alt="`${selectedProject.project_name} ${index + 1}`"
              loading="lazy"
              decoding="async"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Contact + Quote Section -->
    <section id="contact" class="contact">
      <div class="container">
        <div class="section-header">
          <h2>Contact & Get a Quote</h2>
          <div class="underline"></div>
          <p>{{ dynamicContactIntro }}</p>
        </div>
        <div class="contact-content">
          <div class="contact-info">
            <div class="info-item">
              <div class="info-icon">📍</div>
              <div>
                <h4>Address</h4>
                <p>6299 W Contadora Dr, West Valley City, Utah 84128</p>
              </div>
            </div>
            <div class="info-item">
              <div class="info-icon">📞</div>
              <div>
                <h4>Phone</h4>
                <p>+1 (801) 381-9195</p>
              </div>
            </div>
            <div class="info-item">
              <div class="info-icon">✉️</div>
              <div>
                <h4>Email</h4>
                <p>abel@2gnconstruction.com</p>
              </div>
            </div>
            <div class="info-item">
              <div class="info-icon">🕐</div>
              <div>
                <h4>Working Hours</h4>
                <p>Mon - Fri: 7:00 AM - 5:00 PM</p>
                <p>Sat: by appointment only</p>
                <p>Sun: Closed</p>
              </div>
            </div>
          </div>
          <div class="contact-form">
            <form @submit.prevent="handleSubmit">
              <div class="form-group">
                <input type="text" placeholder="Your Name" v-model="form.name" required>
              </div>
              <div class="form-group">
                <input type="email" placeholder="Your Email" v-model="form.email" required>
              </div>
              <div class="form-group">
                <input type="tel" placeholder="Your Phone" v-model="form.phone">
              </div>
              <div class="form-group">
                <input type="text" placeholder="City" v-model="form.city">
              </div>
              <div class="form-group">
                <input type="text" placeholder="State" v-model="form.state">
              </div>
              <div class="form-group">
                <select v-model="form.projectType" required>
                  <option disabled value="">Project Type</option>
                  <option>Residential</option>
                  <option>Commercial</option>
                  <option>Renovation</option>
                  <option>Other</option>
                </select>
              </div>
              <div class="form-group">
                <input type="text" placeholder="Preferred Timeline (e.g., Q2 2026)" v-model="form.timeline">
              </div>
              <div class="form-group">
                <textarea placeholder="Project Details" v-model="form.message" rows="5" required></textarea>
              </div>
              <div class="form-group">
                <input
                  ref="quoteAttachmentInput"
                  type="file"
                  @change="handleAttachmentChange"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                >
                <small class="attachment-help">Optional. Max 5 files, up to 5MB each. Accepted: PDF, PNG, JPG, DOC, DOCX.</small>
              </div>
              <div v-if="recaptchaEnabled" class="form-group">
                <div ref="recaptchaWidget"></div>
              </div>
              <button type="submit" class="btn btn-primary" :disabled="submitting">
                {{ submitting ? 'Sending...' : 'Request Quote' }}
              </button>
              <p v-if="submitMessage" class="form-success">{{ submitMessage }}</p>
              <p v-if="submitError" class="form-error">{{ submitError }}</p>
            </form>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-section">
            <h3>2GN Construction</h3>
            <p>Building excellence, one project at a time.</p>
          </div>
          <div class="footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="#home">Home</a></li>
              <li><a href="#about">About</a></li>
              <li><a href="#services">Services</a></li>
              <li><a href="#projects">Projects</a></li>
            </ul>
          </div>
          <div class="footer-section">
            <h4>Services</h4>
            <ul>
              <li>Residential Construction</li>
              <li>Commercial Building</li>
              <li>Renovation & Remodeling</li>
              <li>Project Management</li>
            </ul>
          </div>
        </div>
        <div class="footer-bottom">
          <p>
            &copy; 2026 2GN Construction. All rights reserved.
            Business Entity No: 12126782-0610
            <a class="footer-cms" href="#/privacy">PRIVACY</a>
            <a class="footer-cms" href="#/admin">ADMIN</a>
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:3000'
  : '';
const RECAPTCHA_SITE_KEY = process.env.VUE_APP_RECAPTCHA_SITE_KEY || '';
const MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_ATTACHMENT_COUNT = 5;
const ALLOWED_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

export default {
  name: 'HomePage',
  data() {
    return {
      menuOpen: false,
      projectsFromDb: [],
      projectImages: [],
      projectsLoading: false,
      isGalleryOpen: false,
      selectedProject: null,
      activeGalleryIndex: 0,
      previousBodyOverflow: '',
      services: [
        {
          id: 1,
          icon: '🏘️',
          title: 'Residential Construction',
          description: 'Residential developments built to your specifications with quality craftsmanship.'
        },
        {
          id: 2,
          icon: '🏢',
          title: 'Commercial Building',
          description: 'Commercial interiors designed for functionality, aesthetics, and long-term value.'
        },
        {
          id: 4,
          icon: '🔨',
          title: 'Renovation & Remodeling',
          description: 'Transform existing spaces with our expert renovation and remodeling services.'
        },
        {
          id: 5,
          icon: '📋',
          title: 'Project Management',
          description: 'Comprehensive project management ensuring timely delivery and budget compliance.'
        },
        {
          id: 6,
          icon: '🎨',
          title: 'Design & Planning',
          description: 'Professional design and planning services from concept to completion.'
        }
      ],
      form: {
        name: '',
        email: '',
        phone: '',
        city: '',
        state: '',
        projectType: '',
        timeline: '',
        message: '',
        attachments: []
      },
      submitting: false,
      submitMessage: '',
      submitError: '',
      recaptchaEnabled: Boolean(RECAPTCHA_SITE_KEY),
      recaptchaWidgetId: null,
      recaptchaToken: ''
    }
  },
  mounted() {
    this.fetchProjectCards();
    this.initializeRecaptcha();
    document.addEventListener('keydown', this.handleGalleryKeydown);
  },
  beforeUnmount() {
    this.unlockBodyScroll();
    delete window.__onRecaptchaLoaded2gn;
    document.removeEventListener('keydown', this.handleGalleryKeydown);
  },
  computed: {
    dynamicHeroSubtitle() {
      const hour = new Date().getHours();
      if (hour < 12) {
        return 'Good morning — professional construction services you can trust.';
      }
      if (hour < 18) {
        return 'Good afternoon — building quality residential and commercial spaces.';
      }
      return 'Good evening — planning tomorrow\'s builds with precision and care.';
    },
    dynamicProjectsIntro() {
      const total = this.projectCards.length;
      if (total > 0) {
        return `Browse ${total} featured project${total === 1 ? '' : 's'} from our team.`;
      }
      return 'Explore our latest work and project progress.';
    },
    dynamicContactIntro() {
      const hour = new Date().getHours();
      if (hour >= 7 && hour < 17) {
        return 'Tell us about your project and we\'ll prepare a fast, accurate estimate today.';
      }
      return 'Tell us about your project and we\'ll get back to you the next business day.';
    },
    projectCards() {
      const resolveId = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const imageByProjectId = new Map();
      this.projectImages.forEach((item) => {
        const imageProjectId = resolveId(item?.projectID ?? item?.projectId ?? item?.project_id);
        if (imageProjectId === null) return;
        if (!imageByProjectId.has(imageProjectId)) {
          imageByProjectId.set(imageProjectId, item);
        }
      });

      return this.projectsFromDb.map((project) => {
        const projectId = resolveId(project?.projectID ?? project?.projectId ?? project?.id);
        const image = projectId === null ? null : imageByProjectId.get(projectId);
        return {
          ...project,
          projectID: project?.projectID ?? project?.projectId ?? project?.id,
          imageID: image ? image.imageID : null
        };
      });
    },
    selectedProjectImages() {
      if (!this.selectedProject) return [];
      const selectedProjectId = Number(
        this.selectedProject?.projectID
        ?? this.selectedProject?.projectId
        ?? this.selectedProject?.id,
      );
      return this.projectImages.filter(
        (image) => Number(image?.projectID ?? image?.projectId ?? image?.project_id) === selectedProjectId,
      );
    }
  },
  methods: {
    initializeRecaptcha() {
      if (!this.recaptchaEnabled) {
        return;
      }

      if (window.grecaptcha?.render) {
        this.renderRecaptchaWidget();
        return;
      }

      window.__onRecaptchaLoaded2gn = () => {
        this.renderRecaptchaWidget();
      };

      if (document.getElementById('google-recaptcha-script')) {
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-recaptcha-script';
      script.async = true;
      script.defer = true;
      script.src = 'https://www.google.com/recaptcha/api.js?onload=__onRecaptchaLoaded2gn&render=explicit';
      document.head.appendChild(script);
    },
    renderRecaptchaWidget() {
      if (!this.recaptchaEnabled || this.recaptchaWidgetId !== null) {
        return;
      }

      if (!this.$refs.recaptchaWidget || !window.grecaptcha?.render) {
        return;
      }

      this.recaptchaWidgetId = window.grecaptcha.render(this.$refs.recaptchaWidget, {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: (token) => {
          this.recaptchaToken = token;
        },
        'expired-callback': () => {
          this.recaptchaToken = '';
        },
        'error-callback': () => {
          this.recaptchaToken = '';
        }
      });
    },
    resetRecaptcha() {
      this.recaptchaToken = '';
      if (this.recaptchaWidgetId !== null && window.grecaptcha?.reset) {
        window.grecaptcha.reset(this.recaptchaWidgetId);
      }
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    async fetchProjectCards() {
      this.projectsLoading = true;
      try {
        const [projectResponse, imageResponse] = await Promise.all([
          fetch(`${API_BASE}/project`),
          fetch(`${API_BASE}/project-image`)
        ]);
        this.projectsFromDb = await projectResponse.json();
        this.projectImages = await imageResponse.json();
      } catch (error) {
        console.error('Failed to load projects', error);
        this.projectsFromDb = [];
        this.projectImages = [];
      } finally {
        this.projectsLoading = false;
      }
    },
    imageUrl(id, options = {}) {
      const params = new URLSearchParams();
      if (options.w) {
        params.set('w', String(options.w));
      }
      if (options.q) {
        params.set('q', String(options.q));
      }
      const query = params.toString();
      return `${API_BASE}/project-image/${id}/file${query ? `?${query}` : ''}`;
    },
    imageSrcSet(id, widths = [], quality = 72) {
      return widths
        .map((width) => `${this.imageUrl(id, { w: width, q: quality })} ${width}w`)
        .join(', ');
    },
    openProjectGallery(project) {
      this.selectedProject = project;
      this.activeGalleryIndex = 0;
      this.isGalleryOpen = true;
      this.lockBodyScroll();
    },
    closeProjectGallery() {
      this.isGalleryOpen = false;
      this.selectedProject = null;
      this.activeGalleryIndex = 0;
      this.unlockBodyScroll();
    },
    lockBodyScroll() {
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    },
    unlockBodyScroll() {
      document.body.style.overflow = this.previousBodyOverflow;
    },
    prevGalleryImage() {
      if (this.selectedProjectImages.length < 2) return;
      const nextIndex = this.activeGalleryIndex - 1;
      this.activeGalleryIndex = nextIndex < 0 ? this.selectedProjectImages.length - 1 : nextIndex;
    },
    nextGalleryImage() {
      if (this.selectedProjectImages.length < 2) return;
      this.activeGalleryIndex = (this.activeGalleryIndex + 1) % this.selectedProjectImages.length;
    },
    setGalleryImage(index) {
      this.activeGalleryIndex = index;
    },
    handleGalleryKeydown(event) {
      if (!this.isGalleryOpen) return;
      if (event.key === 'Escape') {
        this.closeProjectGallery();
      }
      if (event.key === 'ArrowLeft') {
        this.prevGalleryImage();
      }
      if (event.key === 'ArrowRight') {
        this.nextGalleryImage();
      }
    },
    async handleSubmit() {
      this.submitting = true;
      this.submitMessage = '';
      this.submitError = '';
      try {
        if (this.recaptchaEnabled && !this.recaptchaToken) {
          this.submitError = 'Please complete reCAPTCHA.';
          return;
        }

        const formData = new FormData();
        formData.append('name', this.form.name);
        formData.append('email', this.form.email);
        formData.append('phone', this.form.phone || '');
        formData.append('city', this.form.city || '');
        formData.append('state', this.form.state || '');
        formData.append('projectType', this.form.projectType);
        formData.append('timeline', this.form.timeline || '');
        formData.append('message', this.form.message);
        if (this.recaptchaEnabled) {
          formData.append('recaptchaToken', this.recaptchaToken);
        }
        if (this.form.attachments.length > 0) {
          this.form.attachments.forEach((attachment) => {
            formData.append('attachments', attachment);
          });
        }

        const response = await fetch(`${API_BASE}/quote`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          this.submitError = payload?.message || 'Failed to send request.';
          if (this.recaptchaEnabled) {
            this.resetRecaptcha();
          }
          return;
        }

        const payload = await response.json().catch(() => null);
        this.submitMessage = payload?.message || 'Thanks for your request! We will contact you shortly.';
        this.form = {
          name: '',
          email: '',
          phone: '',
          city: '',
          state: '',
          projectType: '',
          timeline: '',
          message: '',
          attachments: []
        };
        if (this.$refs.quoteAttachmentInput) {
          this.$refs.quoteAttachmentInput.value = '';
        }
        if (this.recaptchaEnabled) {
          this.resetRecaptcha();
        }
      } catch (error) {
        console.error('Quote request failed', error);
        this.submitError = 'Failed to send request.';
        if (this.recaptchaEnabled) {
          this.resetRecaptcha();
        }
      } finally {
        this.submitting = false;
      }
    },
    handleAttachmentChange(event) {
      const files = Array.from(event?.target?.files || []);
      if (files.length === 0) {
        this.form.attachments = [];
        return;
      }

      if (files.length > MAX_ATTACHMENT_COUNT) {
        this.submitError = `Too many files. Max ${MAX_ATTACHMENT_COUNT} attachments allowed.`;
        this.form.attachments = [];
        if (this.$refs.quoteAttachmentInput) {
          this.$refs.quoteAttachmentInput.value = '';
        }
        return;
      }

      const hasInvalidType = files.some(
        (file) => !ALLOWED_ATTACHMENT_MIME_TYPES.includes(file.type)
      );
      if (hasInvalidType) {
        this.submitError = 'Invalid file type. Allowed: PDF, PNG, JPG, DOC, DOCX.';
        this.form.attachments = [];
        if (this.$refs.quoteAttachmentInput) {
          this.$refs.quoteAttachmentInput.value = '';
        }
        return;
      }

      const hasLargeFile = files.some(
        (file) => file.size > MAX_ATTACHMENT_SIZE_BYTES
      );
      if (hasLargeFile) {
        this.submitError = 'Attachment too large. Max size is 5MB per file.';
        this.form.attachments = [];
        if (this.$refs.quoteAttachmentInput) {
          this.$refs.quoteAttachmentInput.value = '';
        }
        return;
      }

      this.submitError = '';
      this.form.attachments = files;
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.home-page {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Navigation */
.navbar {
  background: #1a1a1a;
  color: white;
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.navbar .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo h1 {
  font-size: 1.8rem;
  color: #ff6b35;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo-image {
  height: 44px;
  width: auto;
  display: block;
}

.nav-links {
  display: flex;
  list-style: none;
  gap: 2rem;
}

.nav-links a {
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;
}

.nav-links a:hover {
  color: #ff6b35;
}

.menu-toggle {
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
}

/* Hero Section */
.hero {
  height: 600px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-overlay {
  background: rgba(0, 0, 0, 0.4);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-content {
  text-align: center;
  color: white;
  padding: 2rem;
}

.hero-title {
  font-size: 3.5rem;
  margin-bottom: 1rem;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.hero-subtitle {
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: 0.95;
}

.hero-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn {
  padding: 1rem 2rem;
  font-size: 1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.btn-primary {
  background: #ff6b35;
  color: white;
}

.btn-primary:hover {
  background: #e55a2b;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(255,107,53,0.4);
}

.btn-secondary {
  background: transparent;
  color: white;
  border: 2px solid white;
}

.btn-secondary:hover {
  background: white;
  color: #667eea;
}

/* Section Headers */
.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-header h2 {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
}

.underline {
  width: 80px;
  height: 4px;
  background: #ff6b35;
  margin: 0 auto 1rem;
}

.section-header p {
  font-size: 1.1rem;
  color: #666;
}

/* About Section */
.about {
  padding: 5rem 0;
  background: #f8f9fa;
}

.about-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
}

.about-text h3 {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.about-text p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #666;
  margin-bottom: 1rem;
}

.stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 2rem;
}

.stat-item {
  text-align: center;
}

.stat-item h4 {
  font-size: 2.5rem;
  color: #ff6b35;
  margin-bottom: 0.5rem;
}

.stat-item p {
  font-size: 1rem;
  color: #666;
}

.about-image {
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-placeholder {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
}

/* Services Section */
.services {
  padding: 5rem 0;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 320px));
  justify-content: center;
  gap: 2rem;
}

.service-card {
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  text-align: center;
  transition: transform 0.3s;
}

.service-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
}

.service-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.service-card h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.service-card p {
  color: #666;
  line-height: 1.6;
}

/* Projects Section */
.projects {
  padding: 5rem 0;
  background: #f8f9fa;
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.project-card {
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  transition: transform 0.3s;
  cursor: pointer;
}

.project-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
}

.project-image {
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.project-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
}

.project-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.project-info {
  padding: 1.5rem;
}

.project-info h3 {
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
}

.project-category {
  color: #ff6b35;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.project-info p {
  color: #666;
  line-height: 1.6;
}

.gallery-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
}

.gallery-modal {
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  position: relative;
}

.gallery-close {
  position: absolute;
  top: 0.5rem;
  right: 0.75rem;
  border: none;
  background: transparent;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  color: #374151;
}

.gallery-modal-header {
  margin-bottom: 1rem;
  padding-right: 2rem;
}

.gallery-modal-header h3 {
  font-size: 1.5rem;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
}

.gallery-modal-header p {
  color: #6b7280;
}

.gallery-modal-main {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background: #f3f4f6;
}

.gallery-modal-main img {
  width: 100%;
  max-height: 60vh;
  object-fit: cover;
  display: block;
}

.gallery-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
}

.gallery-prev {
  left: 0.75rem;
}

.gallery-next {
  right: 0.75rem;
}

.gallery-thumbnails {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.gallery-thumb {
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
}

.gallery-thumb img {
  width: 100%;
  height: 70px;
  object-fit: cover;
  display: block;
}

.gallery-thumb.active {
  border-color: #ff6b35;
}

.gallery-modal-empty {
  text-align: center;
  color: #6b7280;
  padding: 2rem 1rem;
  background: #f9fafb;
  border-radius: 10px;
}

/* Gallery Section */
.gallery {
  padding: 5rem 0;
  background: #ffffff;
}

.gallery-status {
  text-align: center;
  color: #6b7280;
  margin-top: 1.5rem;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.gallery-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  background: #f4f4f4;
  height: 200px;
}

.gallery-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Contact Section */
.contact {
  padding: 5rem 0;
}

.contact-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.info-item {
  display: flex;
  gap: 1rem;
}

.info-icon {
  font-size: 2rem;
}

.info-item h4 {
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
}

.info-item p {
  color: #666;
  line-height: 1.6;
}

.contact-form {
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 10px;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  font-family: inherit;
  background: #fff;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
}

.form-group textarea {
  resize: vertical;
}

.attachment-help {
  display: block;
  margin-top: 0.35rem;
  color: #666;
  font-size: 0.875rem;
}

.form-success {
  margin-top: 1rem;
  color: #16a34a;
  font-weight: 600;
}

.form-error {
  margin-top: 1rem;
  color: #dc2626;
  font-weight: 600;
}

.btn:disabled {
  opacity: 0.7;
  cursor: default;
}

/* Footer */
.footer {
  background: #1a1a1a;
  color: white;
  padding: 3rem 0 1rem;
}

.footer-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
  justify-items: center;
  text-align: center;
}

.footer-section {
  width: 100%;
  max-width: 280px;
}

.footer-section h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #ff6b35;
}

.footer-section h4 {
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.footer-section p {
  color: #ccc;
  line-height: 1.6;
}

.footer-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-section li {
  margin-bottom: 0.5rem;
  color: #ccc;
}

.footer-section a {
  color: #ccc;
  text-decoration: none;
  transition: color 0.3s;
}

.footer-section a:hover {
  color: #ff6b35;
}

.footer-bottom {
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid #333;
  color: #999;
}

.footer-cms {
  margin-left: 12px;
  font-size: 0.85rem;
  color: #999;
  opacity: 0.6;
  text-decoration: none;
}

.footer-cms:hover {
  opacity: 1;
  color: #ccc;
}

/* Responsive Design */
@media (max-width: 768px) {
  .navbar .container {
    position: relative;
  }

  .nav-links {
    display: none;
    position: absolute;
    top: calc(100% + 8px);
    right: 20px;
    left: 20px;
    background: #1a1a1a;
    border-radius: 10px;
    padding: 0.75rem 1rem;
    flex-direction: column;
    gap: 0.75rem;
    z-index: 200;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  }

  .nav-links.open {
    display: flex;
  }
  
  .menu-toggle {
    display: block;
  }
  
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1.1rem;
  }
  
  .hero-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .about-content,
  .contact-content {
    grid-template-columns: 1fr;
  }
  
  .stats {
    grid-template-columns: 1fr;
  }
  
  .image-placeholder {
    width: 300px;
    height: 300px;
    font-size: 6rem;
  }
  
  .section-header h2 {
    font-size: 2rem;
  }

  .gallery-modal {
    padding: 1rem;
  }

  .gallery-modal-main img {
    max-height: 45vh;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 1.5rem;
  }
  
  .btn {
    padding: 0.8rem 1.5rem;
    font-size: 0.9rem;
  }
}
</style>
